<template>
  <div class="im-input-number" :class="{ 'is-disabled': disabled, 'is-loading': loading, [`im-input-number--${size}`]: size }">
    <div v-if="loading" class="im-input-number__loading">
      <i class="el-icon-loading"></i>
    </div>
    <button 
      class="im-input-number__decrease" 
      :class="{ 'is-disabled': min >= currentValue }"
      type="button"
      @click="decrease"
    >
      <i class="el-icon-minus"></i>
    </button>
    <div class="im-input-number__input-wrap">
      <input
        v-model.number="currentValue"
        type="number"
        class="im-input-number__input"
        :min="min"
        :max="max"
        :disabled="disabled || loading"
        @change="handleInputChange"
        @blur="handleInputBlur"
      />
    </div>
    <button 
      class="im-input-number__increase"
      :class="{ 'is-disabled': max <= currentValue }"
      type="button"
      @click="increase"
    >
      <i class="el-icon-plus"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ImInputNumber',
  props: {
    value: {
      type: [Number, String],
      default: 1
    },
    min: {
      type: Number,
      default: 1
    },
    max: {
      type: Number,
      default: Infinity
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'medium' // small, medium, large
    }
  },
  data() {
    return {
      currentValue: this.value
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.currentValue = val
      }
    }
  },
  methods: {
    decrease() {
      if (this.disabled || this.loading || this.currentValue <= this.min) return
      const newValue = Number(this.currentValue) - 1
      this.updateValue(newValue)
    },
    increase() {
      if (this.disabled || this.loading || this.currentValue >= this.max) return
      const newValue = Number(this.currentValue) + 1
      this.updateValue(newValue)
    },
    handleInputChange(e) {
      const val = e.target.value
      let newValue = Number(val)
      if (isNaN(newValue)) {
        newValue = this.min
      }
      this.updateValue(newValue)
    },
    handleInputBlur() {
      if (this.currentValue < this.min) {
        this.updateValue(this.min)
      } else if (this.currentValue > this.max) {
        this.updateValue(this.max)
      }
    },
    updateValue(val) {
      if (val < this.min) val = this.min
      if (val > this.max) val = this.max
      this.currentValue = val
      this.$emit('input', val)
      this.$emit('change', val)
    }
  }
}
</script>

<style lang="less" scoped>
.im-input-number {
  display: inline-flex;
  align-items: center;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background-color: #fff;
  transition: all 0.2s ease;
  overflow: hidden;
  height: 40px;
  position: relative;

  &:hover:not(.is-disabled):not(.is-loading) {
    border-color: #3D46F5;
    box-shadow: 0 0 0 2px rgba(61, 70, 245, 0.1);
  }

  &.is-disabled {
    background-color: #F3F4F6;
    border-color: #E5E7EB;
    cursor: not-allowed;
    opacity: 0.7;
    
    .im-input-number__input {
      background-color: #F3F4F6;
      color: #9CA3AF;
    }
  }

  &.is-loading {
    cursor: not-allowed;
    
    .im-input-number__loading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      color: #3D46F5;
      font-size: 16px;
    }
  }

  &--small {
    height: 32px;
    
    .im-input-number__decrease,
    .im-input-number__increase {
      width: 32px;
      font-size: 12px;
    }
    
    .im-input-number__input {
      width: 40px;
      font-size: 14px;
    }
  }

  &__decrease,
  &__increase {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #6B7280;
    transition: all 0.2s;
    font-size: 14px;

    &:hover:not(.is-disabled) {
      color: #3D46F5;
      background-color: #EFF6FF;
    }

    &.is-disabled {
      color: #D1D5DB;
      cursor: not-allowed;
    }
    
    &:active:not(.is-disabled) {
      background-color: #DBEAFE;
    }
  }

  &__input-wrap {
    height: 100%;
    border-left: 1px solid #F3F4F6;
    border-right: 1px solid #F3F4F6;
  }

  &__input {
    width: 50px;
    height: 100%;
    border: none;
    text-align: center;
    font-size: 16px;
    color: #111827;
    font-weight: 500;
    outline: none;
    background: transparent;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
